/**
 * Unified Rule Program
 *
 * Provides a single interface for parsing, transforming, and evaluating
 * all three rule types: DSL, Expression, and Natural Language
 *
 * Uses the actual underlying implementations:
 * - Expression: createExpressionProgram, evaluateExpression, compileExpression
 * - DSL: parseDslDetailed
 * - Natural Language: parseNaturalLanguage
 *
 * No reimplementation - delegates to proven components
 */

import { knownFunctionNames } from './expression/known-functions.js';
import {
  extractAllInstructionDependencies,
  extractExpressionDependencies,
} from './dependencies.js';
import {
  compileExpression,
  createExpressionProgram as createExprProgram,
  type ExpressionContext,
  type ExpressionDiagnostic,
  type ExpressionProgram,
  type ExprNode,
  evaluateExpression,
  type ParseExpressionOptions,
  parseExpressionDetailed,
} from './expression.js';
import {
  diagnosticsAgainstSchema as engineDiagnosticsAgainstSchema,
  diagnostics as engineDiagnostics,
  isProgramSource,
} from '@crown/dsl-compiler';
import { dslToExpression } from './dsl/to-expression.js';
import { parseNaturalLanguage } from './natural-expressions.js';
import {
  type DslDiagnostic,
  type DslInstruction,
  parseDslDetailed,
} from './parser.js';

/**
 * Unified diagnostic - works for all rule types
 */
export interface UnifiedDiagnostic {
  message: string;
  severity: 'error' | 'warn' | 'info';
  range?: {
    start: { offset: number; line: number; column: number };
    end: { offset: number; line: number; column: number };
  };
  source: 'dsl' | 'expression' | 'natural';
  /**
   * What the engine calls this problem, and what it offers to do about it.
   *
   * The Rust engine reports a code, a line of help and edits an editor could
   * apply; this shape declared none of them, so a caller reading what the
   * engine actually sent - the corpus checker does, to say WHY a program was
   * refused - could not be type-checked at all. Optional, because a diagnostic
   * raised in TypeScript here carries only a message.
   */
  code?: string;
  help?: string;
  suggestions?: Array<{
    /** What the edit would do, as an editor would offer it. */
    message: string;
    /** The text to put there. */
    replacement: string;
    /** Where the replaced span starts, and how far it runs. */
    index?: number;
    length?: number;
  }>;
}

/**
 * Rule type discriminator
 */
export type RuleType = 'dsl' | 'expression' | 'natural';

/**
 * Parsed representation that works for all types
 */
export interface ParsedRule {
  type: RuleType;
  source: string;

  dsl?: DslInstruction[];
  expression?: ExprNode;
  naturalExpression?: string;

  dependencies: string[];
  diagnostics: UnifiedDiagnostic[];
  isValid: boolean;
}

/**
 * Evaluation result
 */
export interface EvaluationResult {
  success: boolean;
  value?: unknown;
  error?: string;
  diagnostics: UnifiedDiagnostic[];
}

/**
 * Shared evaluation context for all rule types
 */
export interface RuleEvaluationContext {
  value: unknown;
  root: Record<string, unknown>;
  ctx?: Record<string, unknown>;
  fieldName?: string;
}

/**
 * Unified Rule Program
 *
 * Provides incremental parsing, transformation, and evaluation
 * for DSL, Expression, or Natural Language rules
 *
 * Delegates to underlying specialized programs for actual work
 */
export interface UnifiedRuleProgram {
  /** Get current parsed state */
  getSnapshot(): ParsedRule;

  /** Update source text incrementally */
  update(spanStart: number, spanEnd: number, newText: string): ParsedRule;

  /** Replace entire source */
  replaceAll(newSource: string): ParsedRule;

  /** Transform to different representation (e.g., natural -> dsl) */
  transform(targetType: RuleType): ParsedRule | null;

  /** Evaluate the rule - uses actual expression/DSL evaluators */
  evaluate(context: RuleEvaluationContext, debug?: boolean): EvaluationResult;

  /** Get dependencies (reactive field tracking) */
  getDependencies(): string[];

  /** Get underlying program if using expression type */
  getExpressionProgram(): ExpressionProgram | null;
}

/**
 * Options for creating a unified program
 */
export interface UnifiedProgramOptions {
  /** Initial rule type */
  type: RuleType;

  /** Expression parsing options */
  expressionOptions?: ParseExpressionOptions;

  /** Allow automatic type conversion */
  autoConvert?: boolean;
}

/**
 * Convert expression diagnostic to unified format
 */
function exprDiagToUnified(
  diag: ExpressionDiagnostic,
  source: 'expression' | 'natural' = 'expression',
): UnifiedDiagnostic {
  return {
    message: diag.message,
    severity: diag.severity,
    range: {
      start: { offset: diag.index, line: 0, column: diag.index },
      end: {
        offset: diag.index + diag.length,
        line: 0,
        column: diag.index + diag.length,
      },
    },
    source,
  };
}

/**
 * What is wrong with an expression, as the engine that runs it says.
 *
 * The TypeScript parser still builds the tree an editor walks, but it is not
 * the language: it refused forms the engine runs - a function held in a
 * `let`, `foreach` inside a call, a spread argument, a program's `when` and
 * `return` - and a rule it refused could not be evaluated anywhere, however
 * valid. The engine decides. Where it has not loaded yet - a browser before
 * `init` - the parser's own findings stand in.
 *
 * A caller's `documentSchema` and `contextSchema` are checked by the engine
 * too. It walks the whole path through either form of document, knows which
 * names the rule binds itself, and checks a bare field name - `agee > 18` reads
 * `root.agee` - which the parser never did. `treatUnknownPropertyAs` still
 * decides how loudly: a warning unless the caller asks otherwise.
 */
function languageDiagnostics(
  source: string,
  parsed: readonly ExpressionDiagnostic[],
  options: ParseExpressionOptions = {},
): ExpressionDiagnostic[] {
  try {
    const program = isProgramSource(source);
    const engine = engineDiagnostics(source, program).map((d) => ({
      message: d.message,
      severity: d.severity,
      index: d.index,
      length: d.length,
    }));
    return [...engine, ...schemaDiagnostics(source, options)];
  } catch {
    return [...parsed];
  }
}

/** Paths the caller's documents do not declare, as the engine finds them. */
function schemaDiagnostics(
  source: string,
  options: ParseExpressionOptions,
): ExpressionDiagnostic[] {
  const { documentSchema, contextSchema } = options;
  const severity = options.treatUnknownPropertyAs ?? 'warn';
  if (severity === 'ignore') return [];
  if (documentSchema === undefined && contextSchema === undefined) return [];
  return engineDiagnosticsAgainstSchema(source, {
    root: documentSchema,
    ctx: contextSchema,
  })
    .filter((d) => d.code === 'unknown-property')
    .map((d) => ({
      message: d.message,
      severity:
        d.message.includes(' in path root.') && options.treatUnknownRootAsError
          ? 'error'
          : severity,
      index: d.index,
      length: d.length,
    }));
}

/**
 * Convert DSL diagnostic to unified format
 */
function dslDiagToUnified(diag: DslDiagnostic): UnifiedDiagnostic {
  return {
    message: diag.message,
    severity: diag.severity,
    range: diag.range,
    source: 'dsl',
  };
}

/**
 * Parse rule based on type
 */
function parseRule(
  source: string,
  type: RuleType,
  options?: ParseExpressionOptions,
): ParsedRule {
  const result: ParsedRule = {
    type,
    source,
    dependencies: [],
    diagnostics: [],
    isValid: false,
  };

  switch (type) {
    case 'expression': {
      const parsed = parseExpressionDetailed(source, options);
      const found = languageDiagnostics(source, parsed.diagnostics, options);
      result.expression = parsed.ast;
      result.diagnostics = found.map((diag) => exprDiagToUnified(diag));
      result.isValid = found.every((d) => d.severity !== 'error');
      result.dependencies = extractExpressionDependencies(source);
      break;
    }

    case 'dsl': {
      const parsed = parseDslDetailed(source);
      result.dsl = parsed.instructions;
      result.diagnostics = parsed.diagnostics.map(dslDiagToUnified);
      result.isValid =
        parsed.diagnostics.filter((d) => d.severity === 'error').length === 0;

      result.dependencies = extractDslDependencies(parsed.instructions);
      break;
    }

    case 'natural': {
      const expression = parseNaturalLanguage(source);
      if (expression) {
        result.naturalExpression = expression;
        const exprParsed = parseExpressionDetailed(expression, options);
        // The translation is judged by the engine that will run it, as a
        // written expression is.
        const found = languageDiagnostics(
          expression,
          exprParsed.diagnostics,
          options,
        );
        result.expression = exprParsed.ast;
        result.diagnostics = found.map((diag) =>
          exprDiagToUnified(diag, 'natural'),
        );
        result.isValid = found.every((d) => d.severity !== 'error');
        result.dependencies = extractExpressionDependencies(expression);
      } else {
        result.diagnostics.push({
          message: 'Could not parse natural language rule',
          severity: 'error',
          source: 'natural',
        });
        result.isValid = false;
      }
      break;
    }
  }

  return result;
}

/**
 * Extract field dependencies from DSL instructions
 * Uses the shared dependency extraction from dependencies.ts
 */
function extractDslDependencies(instructions: DslInstruction[]): string[] {
  return extractAllInstructionDependencies(instructions);
}

/**
 * Create a unified rule program
 * Delegates to underlying specialized implementations
 */
export function createRuleProgram(
  source: string,
  options: UnifiedProgramOptions,
): UnifiedRuleProgram {
  const currentType = options.type;
  let currentParsed = parseRule(source, currentType, options.expressionOptions);
  let currentSource = source;

  let exprProgram: ExpressionProgram | null = null;
  let compiledExprFn: ((ctx: ExpressionContext) => unknown) | null = null;

  if (currentType === 'expression') {
    exprProgram = createExprProgram(source, options.expressionOptions);
    try {
      compiledExprFn = compileExpression(source);
    } catch (_err) {
      compiledExprFn = null;
    }
  }

  return {
    getSnapshot() {
      return currentParsed;
    },

    update(spanStart: number, spanEnd: number, newText: string) {
      if (spanStart < 0) spanStart = 0;
      if (spanEnd > currentSource.length) spanEnd = currentSource.length;
      if (spanEnd < spanStart) spanEnd = spanStart;

      currentSource =
        currentSource.slice(0, spanStart) +
        newText +
        currentSource.slice(spanEnd);

      if (currentType === 'expression' && exprProgram) {
        const snapshot = exprProgram.update(spanStart, spanEnd, newText);
        currentParsed.source = snapshot.source;
        currentParsed.expression = snapshot.ast;
        const found = languageDiagnostics(
          snapshot.source,
          snapshot.diagnostics,
          options.expressionOptions,
        );
        currentParsed.diagnostics = found.map((diag) =>
          exprDiagToUnified(diag),
        );
        currentParsed.isValid = found.every((d) => d.severity !== 'error');
        currentParsed.dependencies = snapshot.ast
          ? extractExpressionDependencies(currentSource)
          : [];

        compiledExprFn = compileExpression(currentSource);
      } else {
        currentParsed = parseRule(
          currentSource,
          currentType,
          options.expressionOptions,
        );
      }

      return currentParsed;
    },

    replaceAll(newSource: string) {
      currentSource = newSource;

      if (currentType === 'expression' && exprProgram) {
        const snapshot = exprProgram.replaceAll(newSource);
        currentParsed.source = snapshot.source;
        currentParsed.expression = snapshot.ast;
        const found = languageDiagnostics(
          snapshot.source,
          snapshot.diagnostics,
          options.expressionOptions,
        );
        currentParsed.diagnostics = found.map((diag) =>
          exprDiagToUnified(diag),
        );
        currentParsed.isValid = found.every((d) => d.severity !== 'error');
        currentParsed.dependencies = snapshot.ast
          ? extractExpressionDependencies(currentSource)
          : [];

        compiledExprFn = compileExpression(currentSource);
      } else {
        currentParsed = parseRule(
          currentSource,
          currentType,
          options.expressionOptions,
        );
      }

      return currentParsed;
    },

    transform(targetType: RuleType) {
      if (targetType === currentType) {
        return currentParsed;
      }

      if (currentType === 'natural' && targetType === 'expression') {
        if (currentParsed.naturalExpression) {
          return parseRule(
            currentParsed.naturalExpression,
            'expression',
            options.expressionOptions,
          );
        }
        return null;
      }

      if (currentType === 'dsl' && targetType === 'expression') {
        if (!currentParsed.isValid || !currentParsed.dsl) return null;
        return parseRule(
          dslToExpression(currentParsed.dsl),
          'expression',
          options.expressionOptions,
        );
      }

      return null;
    },

    evaluate(context: RuleEvaluationContext, debug = false): EvaluationResult {
      const result: EvaluationResult = {
        success: false,
        diagnostics: [],
      };

      if (!currentParsed.isValid) {
        result.diagnostics = currentParsed.diagnostics;
        result.error = 'Rule has parse errors';
        return result;
      }

      try {
        switch (currentType) {
          case 'expression': {
            const exprCtx: ExpressionContext = {
              value: context.value,
              root: context.root,
              ctx: context.ctx,
            };

            // One way to evaluate. There used to be three - a compiled
            // function, a tree walk over the parsed nodes, and a re-parse -
            // because there were two engines and a fallback between them. The
            // fallback is what let a rule that failed to compile still produce
            // an answer, from a different implementation, and nothing said so.
            const exprResult = compiledExprFn
              ? compiledExprFn(exprCtx)
              : evaluateExpression(currentSource, exprCtx);

            result.success = true;
            result.value = exprResult;
            break;
          }

          case 'dsl': {
            if (!currentParsed.dsl || currentParsed.dsl.length === 0) {
              result.error = 'No DSL instructions';
              return result;
            }

            // Run by the engine, as the expression it means. The rule
            // language had an evaluator of its own, which compared a missing
            // value as JavaScript does - `null >= 5` failed, `null <= 5`
            // passed - so a rule's direction decided a half-filled form.
            const source = dslToExpression(currentParsed.dsl);
            if (debug) console.log('[UnifiedProgram] rule as an expression:', source);
            result.success = true;
            result.value = evaluateExpression(source, {
              value: context.value,
              root: context.root,
              ctx: context.ctx,
            });
            break;
          }

          case 'natural': {
            const expressionSource = currentParsed.naturalExpression;
            if (!expressionSource || !currentParsed.expression) {
              result.error =
                'Natural language rule did not produce a valid expression';
              return result;
            }

            const exprCtx: ExpressionContext = {
              value: context.value,
              root: context.root,
              ctx: context.ctx,
            };

            // One engine. This branch used to walk the parsed nodes with the
            // TypeScript evaluator when there was no compiled function; now the
            // engine compiles and evaluates, and a rule that cannot compile
            // says so rather than being answered by something else.
            //
            // What is evaluated is the expression the natural text translated
            // to - `age >= 18` - not the text itself, which is English and
            // does not parse.
            const exprResult = evaluateExpression(expressionSource, exprCtx);

            result.success = true;
            result.value = exprResult;
            break;
          }
        }
      } catch (err) {
        result.error = err instanceof Error ? err.message : String(err);
        result.diagnostics.push({
          message: result.error,
          severity: 'error',
          source: currentType,
        });
      }

      return result;
    },

    getDependencies() {
      return currentParsed.dependencies;
    },

    getExpressionProgram() {
      return exprProgram;
    },
  };
}

/**
 * Helper to create programs from different sources
 */
export function createDslRuleProgram(
  dsl: string,
  opts?: ParseExpressionOptions,
): UnifiedRuleProgram {
  return createRuleProgram(dsl, { type: 'dsl', expressionOptions: opts });
}

/** A rule program from a raw expression. */
export function createExpressionRuleProgram(
  expr: string,
  opts?: ParseExpressionOptions,
): UnifiedRuleProgram {
  return createRuleProgram(expr, {
    type: 'expression',
    expressionOptions: opts,
  });
}

/**
 * A rule program from English.
 *
 * The English is translated to an expression first, so a phrase that cannot be
 * interpreted produces a program that reports it as invalid rather than one that
 * silently passes.
 */
export function createNaturalRuleProgram(
  natural: string,
  opts?: ParseExpressionOptions,
): UnifiedRuleProgram {
  return createRuleProgram(natural, {
    type: 'natural',
    expressionOptions: opts,
  });
}
